import React, { useState } from "react";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type Transition,
  type Variants,
} from "framer-motion";
import { register, login } from "../api/endpoints";
import { getComplianceRequirements, submitCompliance } from "../api/compliance";
import { navigateTo } from "../utils/navigation";
import { getAuthToken, AuthGateError } from "../api/config";
import { useOptionalUser } from "../contexts/UserContext";
import { EMAIL_RE, EMAIL_HTML_PATTERN, PASS_RE, passwordIssues } from "../utils/regex";
import GoogleLoginButton from "../components/auth/GoogleLoginButton";
import AppleLoginButton from "../components/auth/AppleLoginButton";

// Motion easing matches brand token --ease: cubic-bezier(.22,.61,.36,1)
const EASE = [0.22, 0.61, 0.36, 1] as const;

const REGISTRATIONS_CLOSED_MESSAGE =
  "New signups are temporarily paused. Please check back soon.";

const tick = (
  <svg viewBox="0 0 12 10" style={{ width: 12, display: "block" }} aria-hidden>
    <path d="M1 5l3 3 7-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function RegisterPage(): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  // Each consent is its own decision. There is deliberately no "accept all"
  // control: the previous one ticked the optional marketing box together with
  // the three required legal ones, and it was the only checkbox visible without
  // expanding a collapsed panel — so the default path opted people into
  // marketing as a side effect of accepting the Terms. Bundled consent of that
  // shape is not freely given or specific, which is the standard GDPR test.
  const [consents, setConsents] = useState({
    tos: false,
    privacy: false,
    aiDisclaimer: false,
  });

  // Fail OPEN in the UI: if the config fetch failed, hasn't landed yet, or
  // the field is missing (older backend), treat registrations as enabled.
  // This is presentation only — the server is the real, fail-closed gate and
  // will still reject with 403 registrations_closed if this is stale.
  const userCtx = useOptionalUser();
  const registrationOpen = userCtx?.registrationOpen ?? true;

  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({ email: false, password: false });

  const toggleSingle = (key: "tos" | "privacy" | "aiDisclaimer", checked: boolean) => {
    setConsents((prev) => ({ ...prev, [key]: checked }));
  };

  const emailTrimmed = email.trim();
  const emailValid = EMAIL_RE.test(emailTrimmed);
  const passwordValid = PASS_RE.test(password);
  // Requirements are shown from the start rather than only after a failure, so
  // the first attempt can succeed. passwordIssues already existed in utils and
  // was never called.
  const pwIssues = passwordIssues(password);

  const showEmailError = touched.email && !emailValid && emailTrimmed.length > 0;
  const showPasswordError = touched.password && !passwordValid && password.length > 0;

  const consentsAccepted = consents.tos && consents.privacy && consents.aiDisclaimer;
  const canSubmit =
    emailValid && passwordValid && consentsAccepted &&
    !loading && registrationOpen;

  // A disabled button with no explanation is a dead end: the user has done all
  // the work and nothing says what is missing. Name the blocker instead.
  const blockedReason = ((): string => {
    if (!registrationOpen || loading) return "";
    if (!emailValid || !passwordValid) return "";
    if (!consentsAccepted) return "Accept the required agreements to continue.";
    return "";
  })();

  const prefersReduced = useReducedMotion();
  const spring: Transition = prefersReduced
    ? { duration: 0 }
    : { type: "spring", stiffness: 420, damping: 36, mass: 0.9 };
  const fadeQuick: Transition = prefersReduced
    ? { duration: 0 }
    : { duration: 0.32, ease: EASE };

  const cardVariants: Variants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: spring },
  };
  const brandRowVariants: Variants = {
    hidden: { opacity: 0, y: -6 },
    visible: { opacity: 1, y: 0, transition: spring },
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError("");
    setTouched({ email: true, password: true });
    if (!canSubmit) return;

    try {
      setLoading(true);
      await register({ email: emailTrimmed, password });

      if (!getAuthToken()) {
        try {
          await login(emailTrimmed, password);
        } catch (error) {
          console.warn("Manual login fallback failed:", error);
        }
      }

      try {
        const { requirements } = await getComplianceRequirements();
        const privacyId = requirements.find((r) => r.slug === "privacy-policy")?.id;
        const tosId = requirements.find((r) => r.slug === "terms-of-service")?.id;
        const aiId = requirements.find((r) => r.slug === "ai-disclaimer")?.id;
        const consentTasks: Promise<unknown>[] = [];
        if (privacyId) consentTasks.push(submitCompliance(privacyId));
        if (tosId) consentTasks.push(submitCompliance(tosId));
        if (aiId) consentTasks.push(submitCompliance(aiId));
        await Promise.all(consentTasks);
      } catch (error) {
        console.warn("Failed to submit consents during registration:", error);
      }

      navigateTo(`/email-verification/pending?email=${encodeURIComponent(emailTrimmed)}`);
    } catch (error) {
      console.error(error);
      if (error instanceof AuthGateError && error.code === "REGISTRATIONS_CLOSED") {
        setServerError(REGISTRATIONS_CLOSED_MESSAGE);
      } else {
        setServerError("Registration failed. Please check your details or contact support.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      {/* Atmospheric orb */}
      <div className="orb" aria-hidden style={{ position: "absolute", bottom: -80, right: -80, opacity: 0.45 }} />

      {/* Back to log in */}
      <div className="auth-page__back">
        <button type="button" onClick={() => { navigateTo("/login"); }} className="link t-small" aria-label="Back to log in">
          ← Back
        </button>
      </div>

      {/* Main panel */}
      <motion.div
        style={{ width: "100%", maxWidth: 440, position: "relative", zIndex: 1 }}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Brand row */}
        <motion.div
          variants={brandRowVariants}
          initial="hidden"
          animate="visible"
          className="stack-2"
          style={{ alignItems: "center", marginBottom: "var(--s-5)", userSelect: "none" }}
        >
          <span className="logo logo--lg">kivo<span className="dot" /></span>
          <span className="t-small t-mute">Feel great in your skin</span>
        </motion.div>

        {/* Auth card */}
        <motion.div variants={cardVariants} initial="hidden" animate="visible">
          <div className="card stack-5">
            {/* Heading */}
            <div className="stack-2">
              <h1 className="t-h3" style={{ margin: 0 }}>Create your <em>account</em></h1>
              <p className="t-small t-mute" style={{ margin: 0 }}>Enter your details to continue.</p>
            </div>

            {/* OAuth — one tap, and the account is created server-side if it
                doesn't exist yet. These already shipped on the login page while
                new users, who need them most, were only offered the email form. */}
            <div className="stack-3">
              <GoogleLoginButton />
              <AppleLoginButton />
              <div style={{ display: "flex", alignItems: "center", gap: "var(--s-3)", padding: "var(--s-1) 0" }}>
                <hr className="rule" style={{ flex: 1 }} />
                <span className="t-label" style={{ flexShrink: 0 }}>Or sign up with email</span>
                <hr className="rule" style={{ flex: 1 }} />
              </div>
            </div>

            <form onSubmit={handleSubmit} className="stack-3" noValidate>
              {/* Email */}
              <div className="stack-2">
                <label className="t-small" htmlFor="reg-email" style={{ fontWeight: 500 }}>Email</label>
                <input
                  id="reg-email"
                  className={`form-input${showEmailError ? " form-input--error" : ""}`}
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  inputMode="email"
                  autoComplete="email"
                  pattern={EMAIL_HTML_PATTERN}
                  aria-invalid={showEmailError ? "true" : "false"}
                  onChange={(e) => { setEmail(e.target.value); }}
                  onBlur={() => { setTouched((t) => ({ ...t, email: true })); }}
                  required
                />
                <AnimatePresence initial={false}>
                  {showEmailError && (
                    <motion.p key="email-err" className="field-error"
                      initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                      transition={fadeQuick} style={{ margin: 0 }}>
                      Please enter a valid email address.
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>

              {/* Password. The confirm field is gone: it existed to catch typos
                  in a field nobody could see, which a reveal toggle solves with
                  one field instead of asking for a strict password twice, blind. */}
              <div className="stack-2">
                <label className="t-small" htmlFor="reg-password" style={{ fontWeight: 500 }}>Password</label>
                <div style={{ position: "relative" }}>
                  <input
                    id="reg-password"
                    className={`form-input${showPasswordError ? " form-input--error" : ""}`}
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    autoComplete="new-password"
                    minLength={8}
                    maxLength={64}
                    aria-invalid={showPasswordError ? "true" : "false"}
                    aria-describedby="reg-password-reqs"
                    onChange={(e) => { setPassword(e.target.value); }}
                    onBlur={() => { setTouched((t) => ({ ...t, password: true })); }}
                    style={{ paddingRight: "4.5rem", width: "100%" }}
                    required
                  />
                  <button
                    type="button"
                    className="link t-small"
                    onClick={() => { setShowPassword((v) => !v); }}
                    aria-pressed={showPassword}
                    style={{
                      position: "absolute", right: "var(--s-3)", top: "50%",
                      transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer",
                    }}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>

                {/* Requirements, visible before the first attempt rather than
                    after it. Ticks fill in live as each one is satisfied. */}
                <p id="reg-password-reqs" className="t-small t-mute" style={{ margin: 0 }}>
                  {password.length === 0
                    ? "Needs 8–64 characters with upper and lower case, a number, and a symbol."
                    : pwIssues.length === 0
                      ? "Password meets all requirements."
                      : `Still needs: ${pwIssues.join(", ")}.`}
                </p>
              </div>

              {/* Consents. Flat and always visible: three separate, required
                  decisions with no master toggle and nothing collapsed. The
                  optional marketing checkbox that used to sit here was removed
                  entirely — /v1/auth/register accepts only email, password and
                  platform, so that box was collected and discarded. A consent
                  control that records nothing is worse than no control. */}
              <div className="stack-2" role="group" aria-label="Required agreements">
                <label className="row-3 t-small" style={{ alignItems: "flex-start", cursor: "pointer" }}>
                  <input type="checkbox" checked={consents.tos}
                    onChange={(e) => { toggleSingle("tos", e.target.checked); }} className="sr-only" />
                  <span className={`kv-checkbox${consents.tos ? " kv-checkbox--on" : ""}`}
                    aria-hidden style={{ marginTop: 2, flexShrink: 0 }}>{tick}</span>
                  <span>
                    I agree to the{" "}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="link">Terms of Service</a>{" "}
                    <span className="t-mute">(required)</span>
                  </span>
                </label>

                <label className="row-3 t-small" style={{ alignItems: "flex-start", cursor: "pointer" }}>
                  <input type="checkbox" checked={consents.privacy}
                    onChange={(e) => { toggleSingle("privacy", e.target.checked); }} className="sr-only" />
                  <span className={`kv-checkbox${consents.privacy ? " kv-checkbox--on" : ""}`}
                    aria-hidden style={{ marginTop: 2, flexShrink: 0 }}>{tick}</span>
                  <span>
                    I agree to the{" "}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="link">Privacy Policy</a>{" "}
                    <span className="t-mute">(required)</span>
                  </span>
                </label>

                <label className="row-3 t-small" style={{ alignItems: "flex-start", cursor: "pointer" }}>
                  <input type="checkbox" checked={consents.aiDisclaimer}
                    onChange={(e) => { toggleSingle("aiDisclaimer", e.target.checked); }} className="sr-only" />
                  <span className={`kv-checkbox${consents.aiDisclaimer ? " kv-checkbox--on" : ""}`}
                    aria-hidden style={{ marginTop: 2, flexShrink: 0 }}>{tick}</span>
                  <span>
                    I acknowledge the{" "}
                    <a href="/ai-disclaimer" target="_blank" rel="noopener noreferrer" className="link">AI Disclaimer</a>{" "}
                    <span className="t-mute">(required)</span>
                  </span>
                </label>
              </div>

              {/* Server error */}
              <AnimatePresence initial={false}>
                {serverError && (
                  <motion.p key="server-err" className="field-error"
                    initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                    transition={fadeQuick} style={{ margin: 0 }}>
                    {serverError}
                  </motion.p>
                )}
              </AnimatePresence>

              {/* Registration kill-switch notice: form stays visible, submit disables */}
              {!registrationOpen && (
                <p className="t-small t-mute" role="status" aria-live="polite" style={{ margin: 0 }}>
                  {REGISTRATIONS_CLOSED_MESSAGE}
                </p>
              )}

              {/* Name what is blocking submission instead of leaving a dead
                  disabled button at the end of a completed form. */}
              {blockedReason && (
                <p className="t-small t-mute" role="status" aria-live="polite" style={{ margin: 0 }}>
                  {blockedReason}
                </p>
              )}

              <motion.div whileTap={{ scale: prefersReduced ? 1 : 0.97 }}>
                <button type="submit" className="btn btn--primary btn--full" disabled={!canSubmit}>
                  {loading ? "Creating account…" : "Create account"}
                </button>
              </motion.div>
            </form>

            {/* Secondary nav */}
            <div className="t-small t-mute">
              Already have an account?{" "}
              <button type="button" onClick={() => { navigateTo("/login"); }} className="link">
                Log in
              </button>
            </div>
          </div>

          {/* Legal strip */}
          <p className="t-small t-mute" style={{ margin: "var(--s-5) 0 0", textAlign: "center" }}>
            Review our{" "}
            <button type="button" onClick={() => { navigateTo("/privacy"); }} className="link">Privacy</button>,{" "}
            <button type="button" onClick={() => { navigateTo("/terms"); }} className="link">Terms</button>, and{" "}
            <button type="button" onClick={() => { navigateTo("/refund"); }} className="link">Refund Policy</button>{" "}
            anytime.
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}
